import { Circle as CircleStyle, Fill, Style } from 'ol/style';
import PointGeometry from 'ol/geom/Point';
import { color as d3color } from 'd3-color';
import color from 'client/utils/constants/GDS/js/color';
import Feature from 'ol/Feature';

import { createBufferedPolygonFromLine } from './bufferedStyle';

import {
    degreesToRadians,
    insetSegmentEnds,
    ringsExtentPx,
    segmentBandQuad
} from './aoiStyleGeometry';

import {
    FRAME_PX,
    EM_BASE_FRAME_PX,
    EM_TAPE_FRAME_PX,
    EM_FINISH_FRAME_PX,
    EM_TAPE_STRIPE_PX,
    EM_TAPE_GAP_PX,
    EM_TAPE_ANGLE_DEG,
    EM_YELLOW,
    outsideOnlyStrokeWidthPx
} from 'client/utils/constants/aoiFrameStyle';

import { LINE_STRING, MULTI_LINE_STRING, POINT, POLYGON } from 'client/utils/constants/geometryType';

const MAX_OFFSCREEN_DIM_PX = 8192;
const MAX_OFFSCREEN_AREA_PX = 16777216; // 4096 x 4096

const createBufferedPolygonGeometry = (bufferDistanceMapUnits) => {
    const bufferedPolygonGeometry = (feature) => {
        const geometry = feature?.getGeometry?.();
        if (!geometry) {
            return null;
        }

        const geometryType = geometry.getType?.();
        if (geometryType !== LINE_STRING && geometryType !== MULTI_LINE_STRING && geometryType !== POINT) {
            return null;
        }

        const geometryToBuffer = geometryType === LINE_STRING && geometry.getFlatCoordinates?.().length === 2
            ? new PointGeometry(geometry.getFlatCoordinates())
            : geometry;

        try {
            return createBufferedPolygonFromLine(geometryToBuffer, bufferDistanceMapUnits);
        } catch (e) {
            return null;
        }
    };

    return bufferedPolygonGeometry;
};

const getGeometryTypeFor = (feature) => {
    if (!(feature && feature instanceof Feature)) {
        throw new Error('style: invalid feature type');
    }

    const geometry = feature.getGeometry?.();
    if (!geometry) {
        throw new Error('style: missing geometry');
    }

    const geometryType = geometry.getType?.();
    if (!geometryType) {
        throw new Error('style: invalid geometry type');
    }

    return geometryType;
};

const drawRingPathFromXYPairs = (ctx, coordinates) => {
    if (!coordinates || coordinates.length === 0) {
        return;
    }
    ctx.moveTo(coordinates[0][0], coordinates[0][1]);
    for (let i = 1; i < coordinates.length; i += 1) {
        ctx.lineTo(coordinates[i][0], coordinates[i][1]);
    }
    ctx.closePath();
};

const drawRingPathFromXYPairsOffset = (ctx, coordinates, offsetX, offsetY) => {
    if (!coordinates || coordinates.length === 0) {
        return;
    }
    ctx.moveTo(coordinates[0][0] + offsetX, coordinates[0][1] + offsetY);
    for (let i = 1; i < coordinates.length; i += 1) {
        ctx.lineTo(coordinates[i][0] + offsetX, coordinates[i][1] + offsetY);
    }
    ctx.closePath();
};

export { degreesToRadians };

const drawQuadPath = (ctx, quad) => {
    if (!quad || quad.length !== 4) {
        return;
    }
    ctx.moveTo(quad[0][0], quad[0][1]);
    ctx.lineTo(quad[1][0], quad[1][1]);
    ctx.lineTo(quad[2][0], quad[2][1]);
    ctx.lineTo(quad[3][0], quad[3][1]);
    ctx.closePath();
};

const buildStripeTileCanvas = ({ stripePx, gapPx, stripeColor }) => {
    const period = Math.max(1, (stripePx || 1) + (gapPx || 0));

    const size = Math.max(32, period * 4);

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return canvas;
    }

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = stripeColor;

    for (let y = -size; y < size * 2; y += period) {
        ctx.fillRect(0, y, size, stripePx);
    }

    return canvas;
};

const createOutsideOnlyStrokeStyle = ({
    strokeColor,
    frameWidth,
    lineCap = 'butt',
    lineJoin = 'miter',
    lineDash,
    geometry,
    zIndex = 0
}) => {
    return new Style({
        geometry,
        zIndex,
        renderer: (coordinates, renderState) => {
            const ctx = renderState.context;

            if (!ctx || coordinates.length === 0) {
                return;
            }

            const strokeWidth = outsideOnlyStrokeWidthPx(frameWidth);
            ctx.save();

            // 1) draw centered stroke
            ctx.beginPath();
            for (const coord of coordinates) {
                drawRingPathFromXYPairs(ctx, coord);
            }

            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = strokeWidth;
            ctx.lineCap = lineCap;
            ctx.lineJoin = lineJoin;
            ctx.miterLimit = 2;

            if (ctx.setLineDash) {
                ctx.setLineDash(lineDash || []);
            }
            ctx.stroke();

            // 2) erase polygon interior -> removes inner half of the stroke
            ctx.save();
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.beginPath();
            for (const coord of coordinates) {
                drawRingPathFromXYPairs(ctx, coord);
            }

            try {
                ctx.fill('evenodd');
            } catch (e) {
                ctx.fill();
            }

            ctx.restore();
            ctx.restore();
        }
    });
};

const createOutsideOnlyTapePatternStyle = ({
    stripeColor,
    frameWidth,
    stripePx,
    gapPx,
    angleDeg,
    segmentEndBufferPx = 0,
    lineCap = 'butt',
    geometry,
    lineJoin = 'miter',
    zIndex = 0
}) => {
    // Sub-pixel segments are visually negligible but expensive because each one
    // incurs clip/rotate/fill work on the offscreen canvas.
    const MIN_SEGMENT_LENGTH_PX = 1.5;
    const MIN_SEGMENT_LENGTH_PX_SQ = MIN_SEGMENT_LENGTH_PX * MIN_SEGMENT_LENGTH_PX;

    let offscreenCanvas;
    let offscreenCtx;
    let stripeTile;
    let pattern;
    let patternKey;

    const ensureOffscreen = (width, height) => {
        if (!offscreenCanvas) {
            offscreenCanvas = document.createElement('canvas');
            offscreenCtx = offscreenCanvas.getContext('2d');
        }
        if (!offscreenCanvas || !offscreenCtx) {
            return;
        }

        if (offscreenCanvas.width !== width) {
            offscreenCanvas.width = width;
        }
        if (offscreenCanvas.height !== height) {
            offscreenCanvas.height = height;
        }
    };

    const ensurePattern = () => {
        const key = `${stripeColor}|${stripePx}|${gapPx}`;
        if (pattern && patternKey === key) {
            return;
        }

        stripeTile = buildStripeTileCanvas({ stripePx, gapPx, stripeColor });
        pattern = offscreenCtx.createPattern(stripeTile, 'repeat');
        patternKey = key;
    };

    return new Style({
        geometry,
        zIndex,
        renderer: (coordinates, renderState) => {
            const ctx = renderState.context;

            if (!ctx || coordinates.length === 0) {
                return;
            }

            const extent = ringsExtentPx(coordinates);
            if (!extent) {
                return;
            }

            const strokeWidth = outsideOnlyStrokeWidthPx(frameWidth);

            // Render into a tight offscreen bbox (huge perf win vs full-viewport offscreen)
            const pad = Math.ceil(Math.max(strokeWidth * 2, 8));
            const offW = Math.max(1, Math.ceil((extent.maxX - extent.minX) + pad * 2));
            const offH = Math.max(1, Math.ceil((extent.maxY - extent.minY) + pad * 2));
            const offsetX = -extent.minX + pad;
            const offsetY = -extent.minY + pad;
            const offscreenArea = offW * offH;

            if (
                offW > MAX_OFFSCREEN_DIM_PX ||
                offH > MAX_OFFSCREEN_DIM_PX ||
                offscreenArea > MAX_OFFSCREEN_AREA_PX
            ) {
                return;
            }

            ensureOffscreen(offW, offH);
            if (!offscreenCtx || !offscreenCanvas) {
                return;
            }

            ensurePattern();
            if (!pattern) {
                return;
            }

            const halfWidth = strokeWidth / 2;

            // Clear offscreen
            offscreenCtx.setTransform(1, 0, 0, 1, 0, 0);
            offscreenCtx.clearRect(0, 0, offW, offH);

            // 1) paint stripes
            const offsetRad = degreesToRadians(angleDeg || 0);
            const big = Math.max(offW, offH) * 4;
            const endBuffer = Math.max(0, segmentEndBufferPx || 0);

            offscreenCtx.fillStyle = pattern;
            for (const ring of coordinates) {
                if (!ring || ring.length < 2) {
                    continue;
                }

                const drawSegment = (a, b) => {
                    const segDx = b[0] - a[0];
                    const segDy = b[1] - a[1];
                    if ((segDx * segDx) + (segDy * segDy) < MIN_SEGMENT_LENGTH_PX_SQ) {
                        return;
                    }

                    const aLocal = [a[0] + offsetX, a[1] + offsetY];
                    const bLocal = [b[0] + offsetX, b[1] + offsetY];

                    const insetResult = insetSegmentEnds(aLocal, bLocal, endBuffer);
                    if (!insetResult) {
                        return;
                    }

                    const { a2, b2, dx, dy } = insetResult;

                    const segAngle = Math.atan2(dy, dx);
                    const angleRad = segAngle + offsetRad;

                    const quad = segmentBandQuad(a2, b2, halfWidth, 0);
                    if (!quad) {
                        return;
                    }

                    offscreenCtx.save();
                    offscreenCtx.beginPath();
                    drawQuadPath(offscreenCtx, quad);

                    offscreenCtx.clip();

                    // anchor rotation around the segment start to reduce phase jumps at shared vertices
                    offscreenCtx.translate(a2[0], a2[1]);
                    offscreenCtx.rotate(angleRad);

                    // After rotation, fill a large rect in local space to guarantee coverage of the clipped quad.
                    offscreenCtx.fillRect(-big, -big, big * 2, big * 2);

                    offscreenCtx.restore();
                };

                for (let i = 1; i < ring.length; i += 1) {
                    drawSegment(ring[i - 1], ring[i]);
                }

                // Some rings may not repeat the first coordinate at the end; ensure the closing edge is drawn.
                if (ring.length > 2) {
                    const first = ring[0];
                    const last = ring[ring.length - 1];
                    if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
                        drawSegment(last, first);
                    }
                }
            }

            // 2) mask to the border band (centered stroke region)
            offscreenCtx.save();
            offscreenCtx.globalCompositeOperation = 'destination-in';
            offscreenCtx.beginPath();
            for (const coord of coordinates) {
                drawRingPathFromXYPairsOffset(offscreenCtx, coord, offsetX, offsetY);
            }
            offscreenCtx.strokeStyle = 'rgba(0,0,0,1)';
            offscreenCtx.lineWidth = strokeWidth;
            offscreenCtx.lineCap = lineCap;
            offscreenCtx.lineJoin = lineJoin;
            offscreenCtx.miterLimit = 2;
            offscreenCtx.stroke();
            offscreenCtx.restore();

            // 3) erase interior -> leaves only the outside half of the band
            offscreenCtx.save();
            offscreenCtx.globalCompositeOperation = 'destination-out';
            offscreenCtx.fillStyle = 'rgba(0,0,0,1)';
            offscreenCtx.beginPath();
            for (const coord of coordinates) {
                drawRingPathFromXYPairsOffset(offscreenCtx, coord, offsetX, offsetY);
            }

            try {
                offscreenCtx.fill('evenodd');
            } catch (e) {
                offscreenCtx.fill();
            }

            offscreenCtx.restore();

            // Composite onto the real layer canvas
            ctx.drawImage(offscreenCanvas, extent.minX - pad, extent.minY - pad);
        }
    });
};

export const pointStyle = new Style({
    image: new CircleStyle({
        radius: 5,
        fill: new Fill({
            color: color.black
        })
    })
});

const aoiPalette = Object.freeze({
    styles: Object.freeze({
        border: createOutsideOnlyStrokeStyle({
            strokeColor: d3color(color.black).toString(),
            frameWidth: FRAME_PX,
            lineCap: 'butt',
            zIndex: 0
        }),
        hazardTapeBase: createOutsideOnlyStrokeStyle({
            strokeColor: d3color(color.black).toString(),
            frameWidth: EM_BASE_FRAME_PX,
            lineCap: 'butt',
            lineJoin: 'miter',
            zIndex: 0
        }),
        hazardTape: createOutsideOnlyTapePatternStyle({
            stripeColor: EM_YELLOW,
            frameWidth: EM_TAPE_FRAME_PX,
            stripePx: EM_TAPE_STRIPE_PX,
            gapPx: EM_TAPE_GAP_PX,
            angleDeg: EM_TAPE_ANGLE_DEG,
            segmentEndBufferPx: 5,
            lineCap: 'butt',
            lineJoin: 'miter',
            zIndex: 1
        }),
        hazardTapeFinish: createOutsideOnlyStrokeStyle({
            strokeColor: d3color(color.black).toString(),
            frameWidth: EM_FINISH_FRAME_PX,
            lineCap: 'butt',
            lineJoin: 'miter',
            zIndex: 2
        }),
        enhancedMeasuresBackdrop: new Style({
            fill: new Fill({
                color: d3color(EM_YELLOW).copy({ opacity: 0.5 }).formatRgb()
            })
        })
    })
});

const getLinearSearchStyles = (bufferDistanceMapUnits) => {
    const geometry = createBufferedPolygonGeometry(bufferDistanceMapUnits);

    const styles = {
        border: createOutsideOnlyStrokeStyle({
            geometry,
            strokeColor: d3color(color.black).toString(),
            frameWidth: FRAME_PX,
            lineCap: 'butt',
            zIndex: 0
        }),
        hazardTapeBase: createOutsideOnlyStrokeStyle({
            geometry,
            strokeColor: d3color(color.black).toString(),
            frameWidth: EM_BASE_FRAME_PX,
            lineCap: 'butt',
            zIndex: 0
        }),
        hazardTape: createOutsideOnlyTapePatternStyle({
            geometry,
            stripeColor: EM_YELLOW,
            frameWidth: EM_TAPE_FRAME_PX,
            stripePx: EM_TAPE_STRIPE_PX,
            gapPx: EM_TAPE_GAP_PX,
            angleDeg: EM_TAPE_ANGLE_DEG,
            segmentEndBufferPx: 5,
            lineCap: 'butt',
            zIndex: 1
        }),
        hazardTapeFinish: createOutsideOnlyStrokeStyle({
            geometry,
            strokeColor: d3color(color.black).toString(),
            frameWidth: EM_FINISH_FRAME_PX,
            lineCap: 'butt',
            zIndex: 2
        }),
        enhancedMeasuresBackdrop: (opacity = 0.5) => {
            return new Style({
                geometry,
                fill: new Fill({
                    color: d3color(EM_YELLOW).copy({ opacity }).formatRgb()
                })
            });
        }
    };
    return styles;
};

export const borderStyle = (feature, bufferDistanceMapUnits) => {
    const geometryType = getGeometryTypeFor(feature);
    if (geometryType === LINE_STRING || geometryType === MULTI_LINE_STRING || geometryType === POINT) {
        return [getLinearSearchStyles(bufferDistanceMapUnits).border];
    }
    if (geometryType === POLYGON) {
        return [aoiPalette.styles.border];
    }
    throw new Error('style: invalid geometry type');
};

export const hazardTapeBorderStyle = (feature, bufferDistanceMapUnits) => {
    const geometryType = getGeometryTypeFor(feature);
    if (geometryType === LINE_STRING || geometryType === MULTI_LINE_STRING || geometryType === POINT) {
        const linearStyles = getLinearSearchStyles(bufferDistanceMapUnits);
        return [
            linearStyles.hazardTapeBase,
            linearStyles.hazardTape,
            linearStyles.hazardTapeFinish
        ];
    }

    if (geometryType === POLYGON) {
        return [
            aoiPalette.styles.hazardTapeBase,
            aoiPalette.styles.hazardTape,
            aoiPalette.styles.hazardTapeFinish
        ];
    }

    throw new Error('style: invalid geometry type');
};

export const enhancedMeasuresBackdropStyle = (feature, opacity = 0.5, bufferDistanceMapUnits) => {
    const geometryType = getGeometryTypeFor(feature);
    if (geometryType === LINE_STRING || geometryType === MULTI_LINE_STRING || geometryType === POINT) {
        return [getLinearSearchStyles(bufferDistanceMapUnits).enhancedMeasuresBackdrop(opacity)];
    }

    return [
        new Style({
            fill: new Fill({
                color: d3color(EM_YELLOW).copy({ opacity }).formatRgb()
            })
        })
    ];
};

export const backdropStyle = (feature) => {
    // intentionally does nothing for future implementations
    getGeometryTypeFor(feature);
    return [];
};

export const __test__ = {
    buildStripeTileCanvas,
    createOutsideOnlyStrokeStyle,
    createOutsideOnlyTapePatternStyle,
    drawQuadPath,
    drawRingPathFromXYPairs,
    drawRingPathFromXYPairsOffset
};
